import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconChevronRight } from '@/assets/svgs/chevron-right.svg'
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import { ReactComponent as IconData } from '@/assets/svgs/icon-data.svg'
import { ReactComponent as IconPitstop } from '@/assets/svgs/icon-pitstop.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import { ReactComponent as IconNoMessage } from '@/assets/svgs/no-message.svg'
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from '@/components/Button'
import CardConfigure from '@/componentsDS/Modal/CardConfigure'
import Input from '@/components/Input/index'
import InputSuffixIcon from '@/components/InputSuffixIcon'
import Modal from '@/componentsDS/Modal/Modal'
import Portal from '@/components/Portal'
import StepList from '@/components/StepList/index'
import Stepper from '@/componentsDS/Stepper/Stepper'
import { EnrolmentTypes } from '@/constants/use-case/enrolment'
import useEnrolments from '@/hooks/use-enrolments'
import useModal from '@/hooks/use-modal/index'
import usePage from '@/hooks/use-page'
import usePrincipals from "@/hooks/use-principal/index"
import useSchemaProperties from '@/hooks/use-schema-properties'
import useStepList from '@/hooks/use-step-list'
import useSubscriptions from '@/hooks/use-subscriptions/index'
import { useNavigate, useParams, useQuery } from '@/lib/router-dom'
import PrincipalTableList from "@/pages/use-cases/agent-principal/principal-table"
import API_SUBSCRIPTIONS from '@/services/api/subscriptions'
import { feature } from '@/utils/feature-verifier'
import { featureConstant } from '@/constants/configuration/feature'
import rem from '@/utils/rem'
import { getUseCaseId } from "@/utils/use-case-storage"
import classname from 'classnames'
import { Fragment, useEffect, useMemo, useState } from 'react'
import Loader from 'react-loader-spinner'
import { useSelector } from 'react-redux'

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const { useCaseId, dataElementId, subscriptionType } = useParams()

	const query = useQuery();
	const onBehalfOfPrincipal = query.get('onbehalfofprincipal');
	const forAgent = query.get('agent_flow') ?? "";

	const useCase = useMemo(() => getUseCaseId(useCaseId), [useCaseId]);

	const statuses = {
		approved: "APPROVED",
		pending: "PENDING"
	}

	const [pageTitle, setPageTitle] = useState('Add Participant')
	const [subPageTitle, setSubPageTitle] = useState(useCase.name)

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: ['Dashboard', subPageTitle, "Data Element"] })

	const { goTo } = useNavigate()

	const subscriptionTypes = subscriptionType.replace('add-', '');

	const user = useSelector(state => state.auth.user)

	const [enrolmentType, setEnrolmentType] = useState();

	const pageLimit = 5

	const [sortConfig, setSortConfig] = useState({ key: 'orgName', direction: true })
	const [currentPage, setCurrentPage] = useState(1)

	const [search, setSearch] = useState("")

	const [required, setRequired] = useState(false)
	const [optional, setOptional] = useState(false)
	const [selectedData, setSelectedData] = useState([]);

	const [loading, setLoading] = useState(false)

	const [isEnrolmentOfPrincipal, setIsEnrolmentOfPrincipal] = useState(false)
	const [isRequestForAgentConsent, setIsRequestForAgentConsent] = useState(false);

	const [participantItems, setParticipantItems] = useState([])

	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);

	useEffect(() => {
		if ((onBehalfOfPrincipal === 'consume_on_behalf_of_principal' || onBehalfOfPrincipal === 'provide_on_behalf_of_principal'))
			setIsEnrolmentOfPrincipal(true)
		if(isAgentPrincipalPhase2Enabled && (forAgent === 'providing_agent' || forAgent === 'consuming_agent'))
			setIsRequestForAgentConsent(true)
	}, [])

	/*=================================================
		API
	/================================================*/

	const { findEnrolment: findEnrolmentInfo, currentItemEnrolment: currentEnrolmentItemInfo, setCurrentItemEnrolment: setCurrentEnrolmentItemInfo } = useEnrolments();
	const { itemsEnrolment: enrolmentItems, setItemsEnrolment: setEnrolmentItems, findUniqueEnrolment: fetchEnrolment, metaEnrolment: enrolmentMeta, loadingEnrolment: loadingEnrolmentItem } = useEnrolments();

	const { items: subscriptionsItems, fetch: fetchSubscriptions, loading: loadingSubscription } = useSubscriptions();
	const { requiredFields, optionalFields, getRequiredFields, getOptionalFields } = useSchemaProperties();

	const { getUniqueEnrollmentPrincipal, items: principalItems, setItems: setPrincipalItems, loading: loadingPrincipal } = usePrincipals(
		{ cacheFetchedItem: { identifier: 'id', keys: ['$selected'] } }
	)

	const getEnrolmentInfo = {
		dataElementId: dataElementId,
		useCaseId: useCaseId,
		organizationId: user.organizationId,
	}

	const getAllSubscriptions =
	{
		status: "APPROVED,PENDING",
		dataElementId: dataElementId,
		type: onBehalfOfPrincipal?.toUpperCase() || (subscriptionTypes?.toUpperCase() === EnrolmentTypes.provider ? EnrolmentTypes.consumer : EnrolmentTypes.provider),
		organizationId: user?.organizationId,
		useCaseId: useCaseId,
		isCounterPartyAgent: !!forAgent
	}

	useEffect(() => {
		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			if (isAgentPrincipalPhase2Enabled && onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.provideOnBehalfOfPrincipal) {
				setEnrolmentType(() => {
					return {
						type: "Consumers",
						stepsTitle: "Choose Participant",
						emptyState: "You have not provided any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select one Participant to share data with" : "Only select one Participant that you would like to consume this data",
						continuous: "Providing on behalf (Principal)",
						noun: "Consumer",
						id: EnrolmentTypes.consumer,
						opposite: EnrolmentTypes.provider
					}
				})
			} else if (isAgentPrincipalPhase2Enabled && forAgent) {
				setEnrolmentType(() => {
					return {
						type: "Consumers",
						stepsTitle: "Choose Participant",
						emptyState: "You have not consumed any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select one Participant consuming on behalf of Principals to share data with" : "Only select one Participant that you would like to contribute this data to:",
						continuous: "Contribute",
						noun: "Consumer",
						id: EnrolmentTypes.consumer,
						opposite: EnrolmentTypes.provider,
						agent: EnrolmentTypes.consumeOnBehalfOfPrincipal
					}
				})
			} else {
				setEnrolmentType(() => {
					return {
						type: "Consumers",
						stepsTitle: "Choose Participant",
						emptyState: "You have not consumed any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select Participants to share data with" : "Select consumers that you would like to provide this data element to",
						continuous: "Contributing",
						noun: "Consumer",
						id: EnrolmentTypes.consumer,
						opposite: EnrolmentTypes.provider
					}
				})
			}
		}
		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal) {
				setEnrolmentType(() => {
					return {
						type: "Contributors",
						stepsTitle: "Choose Participant",
						emptyState: "You have not provided any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select one Participant to consume data from" : "Only select one Contributor that you would like to consume this data from",
						continuous: `${isAgentPrincipalPhase2Enabled ? "Consuming on behalf (Principal)": "Consuming"}`,
						noun: "Contributor",
						id: EnrolmentTypes.provider,
						opposite: EnrolmentTypes.consumer
					}
				})
			} else if (isAgentPrincipalPhase2Enabled && forAgent) {
				setEnrolmentType(() => {
					return {
						type: "Contributors",
						stepsTitle: "Choose Participant",
						emptyState: "You have not provided any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select one Participant providing on behalf of Principals to consume data from" : "Only select one Participant that you would like to provide this data to:",
						continuous: "Consume",
						noun: "Contributor",
						id: EnrolmentTypes.provider,
						opposite: EnrolmentTypes.consumer,
						agent: EnrolmentTypes.provideOnBehalfOfPrincipal
					}
				})
			} else {
				setEnrolmentType(() => {
					return {
						type: "Contributors",
						stepsTitle: "Choose Participant",
						emptyState: "You have not provided any data elements",
						noParticipantFound: "No available participant",
						titleHelper: isAgentPrincipalPhase2Enabled ? "Select Participants to consume data from" : 'Select contributors that you would like to provide this data element to',
						continuous: "Consuming",
						noun: "Contributor",
						id: EnrolmentTypes.provider,
						opposite: EnrolmentTypes.consumer
					}
				})
			}
		}


	}, [subscriptionTypes])

	function fetchEnrolmentHandler() {
		if (!enrolmentType && !isEnrolmentOfPrincipal && (isAgentPrincipalPhase2Enabled && !isRequestForAgentConsent)) {
			return
		}
		fetchEnrolment({
			status: statuses.approved,
			useCaseId: useCaseId,
			type: enrolmentType?.agent ?? enrolmentType?.id,
			dataElementId: dataElementId,
			organizationId: "all",
			currentPage: isEnrolmentOfPrincipal ? '' : currentPage,
			pageSize: isEnrolmentOfPrincipal ? '' : pageLimit,
			searchBy: search,
			sortBy: sortConfig.key,
			order: sortConfig.direction === true ? "ASC" : "DESC",
			isCounterParty: isRequestForAgentConsent
		})
	}

	useEffect(() => {
		async function getData() {
			fetchSubscriptions(getAllSubscriptions)
			const tempEnrolment = await findEnrolmentInfo(getEnrolmentInfo)
			setCurrentEnrolmentItemInfo(tempEnrolment[0])

			getRequiredFields(tempEnrolment[0]?.dataElement?.schema)
			getOptionalFields(tempEnrolment[0]?.dataElement?.schema)

			// fetchEnrolmentHandler()
		}
		getData()
	}, [])

	useEffect(() => {
		const dataElementName = currentEnrolmentItemInfo?.dataElement?.name || "";
		setBreadcrumbsItems(['Dashboard', subPageTitle, dataElementName])
	}, [currentEnrolmentItemInfo])

	useEffect(() => {

		if(isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) {
			const stepsArr=[
				{stepTitle : enrolmentType?.stepsTitle},
				{stepTitle : 'Choose Principal'},
				{stepTitle : 'Review & Confirm'}
			]
			// return setSteps([enrolmentType?.stepsTitle, 'Choose Principal', 'Review & Confirm'])
			return setSteps(stepsArr)
		}
		const stepsArr=[
			{stepTitle : enrolmentType?.stepsTitle},
			{stepTitle : 'Review & Confirm'}
		]


		// setSteps([enrolmentType?.stepsTitle, 'Review & Confirm'])
		setSteps(stepsArr)


	}, [enrolmentType, isRequestForAgentConsent])

	/*=================================================
		Step
	/================================================*/

	const [steps, setSteps] = useState([])

	const { step, goToStep, prevStep, nextStep } = useStepList(steps)

	useEffect(() => {
		if (isEnrolmentOfPrincipal){
			const stepsArr=[
				{stepTitle : enrolmentType?.stepsTitle},
				{stepTitle : 'Choose Principal'},
				{stepTitle : 'Review & Confirm'}
			]
			setSteps(stepsArr)
		}

			// setSteps([enrolmentType?.stepsTitle, 'Choose Principal', 'Review & Confirm'])
	}, [isEnrolmentOfPrincipal])

	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleCancel, show: showCancel, hide: hideCancel } = useModal()

	/*=================================================
		Participant List
	/================================================*/

	useEffect(() => {
		if (loadingEnrolmentItem || loadingSubscription)
			return

		let approvedIds = []

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			approvedIds = subscriptionsItems.map(subscription => subscription?.consumer?.organization.id)
		}

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			approvedIds = subscriptionsItems.map(subscription => subscription?.provider?.organization.id)
		}

		const filterData = enrolmentItems.filter((item) => !approvedIds.includes(item?.organization?.id));
		setParticipantItems(filterData)
	}, [enrolmentItems, subscriptionsItems])

	/*=================================================
		Participant Search
	/================================================*/
	const [participantSearchBoxValue, setParticipantSearchBoxValue] = useState("")

	function handleParticipantSearch(e) {
		setParticipantSearchBoxValue(e.target.value)
	}

	/*=================================================
		Principal Search
	/================================================*/
	const [principalSearchBoxValue, setPrincipalSearchBoxValue] = useState("")

	function handlePrincipalSearch(e) {
		setPrincipalSearchBoxValue(e.target.value)
	}

	/*=================================================
		Principal List
	/================================================*/

	function getPrincipalSortedData() {
		if (isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent)) {
			let type = onBehalfOfPrincipal?.toUpperCase() || subscriptionTypes.toUpperCase();
			if(isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) {
				type = enrolmentType.opposite.toUpperCase();
			}
			getUniqueEnrollmentPrincipal({
				useCaseId: useCaseId,
				dataElementId: dataElementId,
				type: type,
				participantId: selectedData?.organization?.id
			})
		}
	}

	const getPrincipalIds = (principalItems) => {
		const principalIds =
			principalItems?.length &&
			principalItems
				.filter((s) => s.$selected === true)
				.map((s, index) => {
					return {
						principalId: s?.id,
					};
				});
		return principalIds;
	};

	/*=================================================
		Sort
	/================================================*/

	function handleSort(key, type) {
		let direction = sortConfig.direction
		if (sortConfig.key === key) {
			direction = !direction
		} else {
			direction = true
		}

		const sortDirection = direction === true ? "asc" : "desc";

		setSortConfig({ key, direction })
	}

	/*=================================================
		Paginate
	/================================================*/

	useEffect(() => {
		fetchEnrolmentHandler()
	}, [currentPage, sortConfig, search, enrolmentType])

	function goToPrev() {

		if (currentPage === 1) {
			return
		}

		setCurrentPage(prev => prev - 1)
	}

	function goToNext() {
		if (currentPage === enrolmentMeta?.pageCount) {
			return
		}

		setCurrentPage(prev => prev + 1)
	}

	useEffect(() => {
		if (currentPage > enrolmentMeta?.pageCount) {
			setCurrentPage(enrolmentMeta?.pageCount || 1)
		}
	}, [enrolmentItems])

	/*=================================================
		Datatable
	/================================================*/

	const DataTable = ({ data, type }) => {
		function getClassName(name) {
			if (!sortConfig) {
				return
			}
			return sortConfig.key === name ? (sortConfig.direction === true ? "is-asc" : "is-desc") : ""
		}
		function getStartPage(pageSize, pageNo) {
			return pageSize * pageNo;
		}

		function getPageLabel(total, pageSize, pageNr) {
			const start = Math.max(
				getStartPage(pageSize, pageNr),
				0
			);
			const end = Math.min(
				getStartPage(pageSize, pageNr + 1),
				total
			);

			return `${start + 1} - ${end}`;
		}


		function handleCurrentInput(e) {
			const value = e.target?.value === "" ? 1 : e.target.value
			const current = parseInt(value)

			if (current < 1) {
				return setCurrentPage(1)
			}

			if (current > enrolmentMeta.pageCount) {
				return setCurrentPage(enrolmentMeta.pageCount)
			}

			setCurrentPage(current)
		}

		const NoMessage = () => {
			return (
				<div className="data-table__empty__icon">
					<IconNoMessage />
					<span>{isAgentPrincipalPhase2Enabled ? enrolmentType?.noParticipantFound : enrolmentType?.emptyState}</span>
				</div>
			)
		}

		let approvedIds = []

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			approvedIds = subscriptionsItems.map(subscription => subscription?.consumer?.organization.id)
		}

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			approvedIds = subscriptionsItems.map(subscription => subscription?.provider?.organization.id)
		}

		function handleSelect(participant, type) {
			if (type === 'select') {
				const selectedItem = data.filter((item) => item.id === participant.id)
				const newItem = {
					id: selectedItem[0]?.id,
					orgId: selectedItem[0]?.organization?.id,
					name: selectedItem[0]?.organization?.name,
					image: selectedItem[0]?.organization?.image
				}
				const updatedData = [...selectedData, newItem]
				setSelectedData(updatedData);
			} else if (type === 'unselect') {
				const updatedSelectionList = selectedData.filter((item) => item.id !== participant.id);
				setSelectedData(updatedSelectionList);
			}
		}

		function clearParticipantSelection() {
			setSelectedData([]);
		}

		const notApprovedData = data.filter((item) => !approvedIds.includes(item?.organization?.id));
		const list = notApprovedData.map((item, index) => {
			return (
				<div className="data-table__table__body__row" key={index}>
					<div className="data-table__table__body__content large data-table__table__body__content--text data-table__table__body__content--heavy">
						<div className="data-table__table__body__content__avatar-row">
							<div className="data-table__table__body__content__avatar" style={{ backgroundImage: `url(${item?.organization?.image})` }}></div>
							<div className="data-table__table__body__content__text">
								<div className="data-table__table__body__content__top">
									{item?.organization?.name}
								</div>
							</div>
						</div>
					</div>
					<div className="data-table__table__body__content data-table__table__body__content--text">
						{
							item?.organization?.personas?.map((persona, index) => {
								return (
									<span key={index}>{persona.personaName}{index + 1 === item?.organization?.personas.length ? '' : ', '}</span>
								)
							})
						}
					</div>
					<div className="data-table__table__body__content data-table__table__body__content--text data-table__table__body__content--end-button">
						{
							approvedIds.includes(item?.organization?.id) && (
								<Button text="Selected" disabled={true} />
							)
						}
						{
							!approvedIds.includes(item?.organization?.id) && (
								<>
									{
										(selectedData && selectedData.some((data) => data.id === item.id)) ?
											(
												<Button text="Selected" submit={() => handleSelect(item, 'unselect')} />
											) : (
												<Button text="Select" ghost submit={() => handleSelect(item, 'select')} />
											)
									}
								</>

							)
						}
					</div>
				</div>
			)
		})

		const Lists = () => {
			return (
				loadingEnrolmentItem || loadingSubscription ? (
					<div className="data-table__empty data-loader">
						<Loader
							className="loadingSpinner__position__center"
							data-testid="loadingSpinner__position__center"
							type="ThreeDots"
							color="grey"
							height={10}
							width={15}
						/>
					</div>
				) : (list.length > 0 ? list :
					<div className="data-table__empty">
						<NoMessage />
					</div>
				)
			)
		}

		return (
			<div className="data-table__content">
				<div className="data-table__content__cols">
					<div className="data-table__content__col">
						<div className="data-table__table">
							<div className="data-table__table__head">
								<div className="data-table__table__head__row">
									<div className="data-table__table__head__row__left data-table__table__head__row__left--full-width request-enrolment__search-box">
										<InputSuffixIcon maxWidth="310" placeholder="Search" icon={<IconSearch />}
											defaultValue={search || ''}
											onChange={(e) => { setSearch(e.target.value) }}
										/>
										<div className="data-table-participant__search-box__selection">{selectedData.length} Selected</div>
										<Button text="Reset Selection" noborder addClass={"data-table-participant__search-box__clear-selection"} submit={() => clearParticipantSelection()} />
									</div>
								</div>
								<div className="data-table__table__head__row">
									<div
										className={`data-table__table__head__title data-table__table__head__title__sort-order ${getClassName("orgName")}`}
										id="orgName" onClick={
											(e) => {
												handleSort('orgName', type)
											}
										}>
										{enrolmentType?.type} <span className="data-table__table__head__icon__sort-order"></span>
									</div>
									<div className="data-table__table__head__title data-table__table__head__title--end-button"></div>
								</div>
							</div>

							<div className="data-table__table__body">
								<Lists />
							</div>

							{
								list.length > 0 && (
									<div className="data-table__table__footer">
										<div className="data-table__table__footer__row">
											<div className="data-table__table__footer__row__left">
												<div className="data-table__table__pagination">
													<input data-testid="pagination-input" className="data-table__table__pagination__page" type="number" value={currentPage} onChange={(e) => handleCurrentInput(e)} min="1" />
													<span className="data-table__table__pagination__page__meta"> of {enrolmentMeta?.pageCount || 1} pages </span>
													<button data-testid="pagination-prev" className="data-table__table__pagination__prev" onClick={() => goToPrev()} disabled={currentPage === 1}><IconChevronDown /></button>
													<button data-testid="pagination-next" className="data-table__table__pagination__next" onClick={() => goToNext()} disabled={currentPage === enrolmentMeta?.pageCount}><IconChevronDown /></button>
												</div>
											</div>
											<div className="data-table__table__footer__row__right">
												<div className="data-table__table__meta">
													Showing {getPageLabel(notApprovedData?.length, pageLimit, currentPage - 1)} of {notApprovedData?.length || 0} entries
												</div>
											</div>
										</div>
									</div>
								)
							}
						</div>
					</div>
				</div>
			</div>
		)
	}

	/*=================================================
		Navigate
	/================================================*/

	function onBack() {
		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.provideOnBehalfOfPrincipal) {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/provide_on_behalf_of_principal`)
			} else {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/provider`)
			}
		}

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal) {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/consume_on_behalf_of_principal`)
			} else {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/consumer`)
			}
		}
	}

	/*=================================================
		Additional
	/================================================*/

	let timeout = null

	const requiredAction = classname({
		'layout-card-action': true,
		'layout-card-action--active': required
	})

	const optionalAction = classname({
		'layout-card-action': true,
		'layout-card-action--active': optional
	})

	/*=================================================
		API Action Functions
	/================================================*/

	async function confirm() {
		if (loading) {
			return
		}

		setLoading(true)

		let payload
		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.provideOnBehalfOfPrincipal) {
				payload = {
					consumerOrgId: selectedData?.organization?.id,
					providerOrgId: user?.organizationId,
					dataElementId: dataElementId,
					useCaseId: useCaseId,
					providerPrincipals: getPrincipalIds(principalItems)
				}
			} else if (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) {
				payload = {
					consumerOrgId: selectedData?.organization?.id,
					providerOrgId: user?.organizationId,
					dataElementId: dataElementId,
					useCaseId: useCaseId,
					consumerPrincipals: getPrincipalIds(principalItems)
				}
			} else {
				payload = selectedData?.map(item => {
					return {
						consumerOrgId: item.orgId,
						providerOrgId: user?.organizationId,
						dataElementId: dataElementId,
						useCaseId: useCaseId
					}
				})
			}
		}

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal) {
				payload = {
					consumerOrgId: user?.organizationId,
					providerOrgId: selectedData?.organization?.id,
					dataElementId: dataElementId,
					useCaseId: useCaseId,
					consumerPrincipals: getPrincipalIds(principalItems)
				}
			} else if ((isAgentPrincipalPhase2Enabled && isRequestForAgentConsent)) {
				payload = {
					consumerOrgId: user?.organizationId,
					providerOrgId: selectedData?.organization?.id,
					dataElementId: dataElementId,
					useCaseId: useCaseId,
					providerPrincipals: getPrincipalIds(principalItems)
				}
			} else {
				payload = selectedData?.map(item => {
					return {
						consumerOrgId: user?.organizationId,
						providerOrgId: item.orgId,
						dataElementId: dataElementId,
						useCaseId: useCaseId
					}
				})
			}
		}

		const [error, response] = await API_SUBSCRIPTIONS.create(payload)

		setLoading(false)

		if (error) {
			return console.log(error)
		}

		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.consumer) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.provideOnBehalfOfPrincipal) {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/provide_on_behalf_of_principal`)
			} else {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/provider`)
			}

		}
		if (subscriptionTypes.toUpperCase() === EnrolmentTypes.provider) {
			if (onBehalfOfPrincipal?.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal) {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/consume_on_behalf_of_principal`)
			} else {
				return goTo(`/use-cases/${useCaseId}/data-elements/${dataElementId}/consumer`)
			}
		}
	}

	/*=================================================
		Principal Confirmation
	/================================================*/

	const SelectedRow = ({ items }) => {
		return items?.length && items.filter((s) => s.$selected === true).map((s, index) => {
			return (
				<Fragment key={index}>
					<div className="subscription-principal layout-request__tile__content__row">
						<div className="layout-request__tile__content__col">
							<div
								className="layout-request__tile__content__avatar "
								style={{
									backgroundImage: `url(${s?.organisation?.iconUrl})`,
								}}
							>
								{!s?.organisation?.iconUrl && <IconPitstop />}
							</div>
							<div className="enrolment-request__content__system-info">
								<div className="enrolment-request__systme-name">
									{s?.name}
								</div>
								{s?.uen ? <div className="enrolment-request__principal-UEN">UEN: {s?.uen}</div> : <></>}
							</div>
						</div>
					</div>
				</Fragment>
			);
		});
	}

	return (
		<>
			{/* <div className="title-header__back" onClick={onBack}>
				<div className=""><IconPrevious /></div>
				<div className="title-header__back__text link">Back</div>
			</div> */}

			<div className="add-subscription-header title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
				<div className="title-header__right">
				</div>
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<hr />

			{(isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent)) ?
				<>
					{step === 1 &&
						<>
							<div className="layout-default__desc__data__element">{enrolmentType?.titleHelper}</div>
							<div className="use-cases__search__box">
								<Input
									maxWidth="334"
									fullWidth
									placeholder={`Search by ${isAgentPrincipalPhase2Enabled ? "Participant" : "contributors"} name`}
									prefix={<IconSearch />}
									value={participantSearchBoxValue || ""}
									onChange={(e) => { handleParticipantSearch(e) }}
									cancelIcon={<IconClose />}
								/>
							</div>
							<PrincipalTableList
								principals={participantItems}
								setPrincipals={setParticipantItems}
								setCurrentParticipants={(data) => { setSelectedData(data) }}
								searchPrincipal={participantSearchBoxValue}
								pagination={true}
								titleHeader={enrolmentType?.type}
								inputType='radio'
								loader={loadingEnrolmentItem || loadingSubscription}
								emptyText={enrolmentType?.emptyState}
								emptySearchText="No participant found"
							/>
						</>
					}

					{step === 2 &&
						<>
							<div className="use-cases__search__box">
								<Input
									maxWidth="334"
									fullWidth
									placeholder="Search by UEN or organisation name"
									prefix={<IconSearch />}
									value={principalSearchBoxValue || ""}
									onChange={(e) => { handlePrincipalSearch(e) }}
									cancelIcon={<IconClose />}
								/>
								<div>
									{principalItems?.filter(s => s?.$selected)?.length > 0 && (
										<span>{principalItems?.filter(s => s?.$selected)?.length || 0} Selected</span>
									)}
								</div>
							</div>
							<PrincipalTableList
								principals={principalItems}
								setPrincipals={setPrincipalItems}
								searchPrincipal={principalSearchBoxValue}
								pagination={true}
								titleHeader="Principals"
								loader={loadingPrincipal}
							/>
						</>
					}
				</>
				:
				<>
					{step === 1 &&
						<>
							<div className="layout-default__desc__data__element">{enrolmentType?.titleHelper}</div>
							<DataTable data={enrolmentItems || []} />
						</>
					}
				</>
			}

			{((isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent)) ? step === 3 : step === 2) &&
				<>
					<div className="layout-card">
						<div className="layout-card__left">
							<div className="layout-card__block">
								<div className="layout-card__block__header">
									<div className="layout-card__block__header__left">
										<div className="layout-card__header__text">
											{enrolmentType?.continuous}
										</div>
									</div>
									<div className="layout-card__block__header__right">
										{/* <Button ghost text="Edit" submit={() => goToStep(1)} /> */}
									</div>
								</div>
								<div className="layout-card__block__content">
									<div className="layout-card__block__content-rows">
										<div className="layout-card__block__content-row">
											<div className="layout-card__block__content-row__top">
												<div className="layout-card__block__content-row__top__left">
													<div className="layout-card__title">
														<div className="layout-card__title__avatar">
															<IconData />
														</div>
														<div className="layout-card__title__text">
															{currentEnrolmentItemInfo?.dataElement?.name}
														</div>
													</div>
												</div>
											</div>
										</div>
										<div className="layout-card__block__content-row">
											<div className="layout-card__block__content-row__top">
												<div className="layout-card__block__content-row__top__left">
													<div className="layout-card__content__text--bold layout-card__content__text--grey">
														Data Fields
													</div>
												</div>
											</div>
										</div>
										<div className="layout-card__block__content-row">
											<div className="layout-card__block__content-row__top">
												<div className="layout-card__block__content-row__top__left">
													<div className="layout-card__content__text--bold">
														Required
													</div>
												</div>
												<div className="layout-card__block__content-row__top__right">
													<div className="layout-card__content__text--bold layout-card__content__text--grey">
														{requiredFields?.length || ""}
													</div>
													{
														requiredFields?.length > 0 && (
															<span data-testid="expand-required-field" className={requiredAction} onClick={() => setRequired(!required)}>
																<IconChevronRight />
															</span>
														)
													}
												</div>
											</div>
											<div className="layout-card__block__content-row__bottom">
												<div className="layout-card__content_rows">
													{required && requiredFields?.map((item, index) => (
														<div className="layout-card__content_row" key={index}>
															<div className="layout-card__content__text">
																{item.name}
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
										<div className="layout-card__block__content-row">
											<div className="layout-card__block__content-row__top">
												<div className="layout-card__block__content-row__top__left">
													<div className="layout-card__content__text--bold">
														Optional
													</div>
												</div>
												<div className="layout-card__block__content-row__top__right">
													<div className="layout-card__content__text--bold layout-card__content__text--grey">
														{optionalFields?.length || ""}
													</div>
													{
														optionalFields?.length > 0 && (
															<span data-testid="expand-optional-field" className={optionalAction} onClick={() => setOptional(!optional)}>
																<IconChevronRight />
															</span>
														)
													}
												</div>
											</div>
											<div className="layout-card__block__content-row__bottom">
												{optional && optionalFields?.map((item, index) => (
													<div className="layout-card__content_row" key={index}>
														<div className="layout-card__content__text">
															{item.name}
														</div>
													</div>
												))}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="layout-card__right">
							{isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) ?
								<>
									<div className="layout-card__block layout-card__block--list">
										<div className="layout-card__block__header">
											<div className="layout-card__block__header__left">
												<div className="layout-card__header__text">
													Selected Participant
												</div>
											</div>
											<div className="layout-card__block__header__right">
												<Button ghost text="Edit" submit={() => goToStep(1)} />
											</div>
										</div>
										<div className="layout-request__title__content__rows">
											<div className="layout-request__tile__content__row">
												<div className="layout-request__tile__content__col">
													<div className="layout-request__tile__content__avatar">
														<IconPitstop />
													</div>
													<div className="enrolment-request__content__system-info">
														<div className="enrolment-request__systme-name">
															{selectedData?.organization?.name}
														</div>

													</div>
												</div>
											</div>
										</div>
									</div>

									<div className="layout-card__block layout-card__block--list">
										<div className="layout-card__block__header">
											<div className="layout-card__block__header__left">
												<div className="layout-card__header__text">
													Selected Principal
												</div>
											</div>
											<div className="layout-card__block__header__right">
												<Button ghost text="Edit" submit={() => goToStep(2)} />
											</div>
										</div>
										<div className="layout-request__title__content__rows">
											<SelectedRow items={principalItems} />
										</div>
									</div>
								</>
								:
								<div className="layout-card__block layout-card__block--list">
									<div className="layout-card__block__header">
										<div className="layout-card__block__header__left">
											<div className="layout-card__header__text">
												{enrolmentType?.type}
											</div>
										</div>
										<div className="layout-card__block__header__right">
											<Button ghost text="Edit" submit={() => goToStep(1)} />
										</div>
									</div>
									<div className="layout-card__block__content--layer">
										<div className="layout-card__block__content__rows">
											{
												selectedData?.map((item) => {
													return (
														<div className="layout-card__block__content__row" key={item?.id}>
															<div className="layout-card__block__content__cols">
																<div className="layout-card__block__content__col">
																	<div className="layout-card__content__avatar" style={{ backgroundImage: `url(${item?.image})` }}></div>
																	<div className="layout-card__content__text--bold">{item?.name}</div>
																</div>
															</div>
														</div>
													)
												})
											}
										</div>
									</div>
								</div>
							}
						</div>
					</div>
				</>
			}
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<div className="layout-default--margin-right">
							<Button text="Cancel" ghost noborder submit={() => onBack()} />
						</div>
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						{step > 1 &&
							<div className="layout-default--margin-right">
								<Button text="Previous" ghost submit={() => {
									prevStep()
								}} />
							</div>
						}

					{isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) ?
						<>
							{step === 1 &&
								<Button text="Next"
									disabled={
										!participantItems.some((item) => item.$selected === true)
									}
									submit={async () => {
										try {
											nextStep()
											getPrincipalSortedData();
										} catch (err) {
											console.log({ err });
										}
									}} />
							}
							{step === 2 &&
								<Button text="Next"
									disabled={
										!principalItems.some((item) => item.$selected === true)
									}
									submit={() => {
										nextStep()
									}} />
							}

						</>
						:
						<>
							{step === 1 &&
								<Button text="Next" disabled={!selectedData?.length} submit={() => nextStep()} />
							}

						</>
					}

					{(isEnrolmentOfPrincipal || (isAgentPrincipalPhase2Enabled && isRequestForAgentConsent) ? step === 3 : step === 2) &&
						<Button text="Save"
							submit={() => confirm()} full disabled={loading} loading={loading}
						/>
					}

					</div>
				</div>
			</div>

			<Modal visible={visibleCancel} variant='medium' close={hideCancel}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Cancel Simulation
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div style={{ fontSize: rem(14), textAlign: 'center' }}>Are you sure you want to cancel?</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Cancel"
								submit={() => {
									hideCancel()
								}}
							/>
							<span style={{ width: rem(10), display: 'inline-block' }}></span>
							<Button text="Confirm"
								submit={() => {
									onBack()
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
		</>
	)
}
export default Page
